import type { ValueOf } from 'type-fest';
import type { Brand } from './types';
import { initZanalytics as initZillowAnalytics, isInitialized } from '@zillow/universal-analytics';
import { getCurrentEnvForClientSide, PRODUCTION } from '@zg-rentals/environment-utils/client';
import { hasDeclinedFunctionalConsent } from '@zg-rentals/datagrail';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';
import { clientOnly, getAnalyticsErrorType, getCountName, getLogPrefix, isAnalyticsDisabled } from './utils';
import type { ConsentWindow } from './utils';

const API_HOSTS = {
  prod: 'e.zg-api.com',
  test: 'analytics-zg-api.test.zillow.net',
} as const;

const BRAND_SPECIFIC_FIELDS = {
  zillow: {
    apiKey: { prod: 'z_prod', test: 'z_test' },
    writeKey: { prod: 'zrm_prod_web', test: 'zrm_dev_web' },
  },
  hotpads: {
    apiKey: { prod: 'hp_prod_web', test: 'hp_dev_web' },
    writeKey: { prod: 'hp_prod_web_nl', test: 'hp_dev_web_nl' },
  },
} as const;

function createConfig(brandedFields: ValueOf<typeof BRAND_SPECIFIC_FIELDS>) {
  const isProd = getCurrentEnvForClientSide() === PRODUCTION;
  return {
    apiKey: isProd ? brandedFields.apiKey.prod : brandedFields.apiKey.test,
    apiHost: isProd ? API_HOSTS.prod : API_HOSTS.test,
    secure: true,
    writeKey: isProd ? brandedFields.writeKey.prod : brandedFields.writeKey.test,
  };
}

let initializedBrand: Brand | undefined;

/**
 * The API key, write key and API host for the brand analytics was initialized with, or undefined before `initialize` runs.
 * Events pass them explicitly because the SDK otherwise uses the config of whoever loaded it first, e.g. page frame's
 * on page frame hosts, whose build-time prod page frame points QA pages at the prod API host.
 */
export function getClickstreamEventOptions() {
  if (!initializedBrand) return undefined;
  const { apiKey, writeKey, apiHost } = createConfig(BRAND_SPECIFIC_FIELDS[initializedBrand]);
  return { apiKey, writeKey, apiHost };
}

function getAnalyticsConfig(brand: keyof typeof BRAND_SPECIFIC_FIELDS) {
  return {
    [brand]: createConfig(BRAND_SPECIFIC_FIELDS[brand]),
  };
}

// Mirrors page-frame-content's zillowAnalytics.js; returns true when analytics is disabled (the SDK no-ops every call while `disable-analytics` is set)
function applyConsentFlag() {
  const consentWindow = window as ConsentWindow;

  if (hasDeclinedFunctionalConsent()) {
    consentWindow['disable-analytics'] = true;
  } else if (consentWindow['disable-analytics'] === undefined) {
    consentWindow['disable-analytics'] = false;
  }

  return isAnalyticsDisabled();
}

// gives @zillow/universal-analytics a callable window.ua so calls are dropped silently instead of retried and logged as dropped
function stubUa() {
  const uaWindow = window as Window & { ua?: () => void };

  uaWindow.ua ??= () => {};
}

// Returns whether this call loaded the SDK, as opposed to finding it already initialized or consent declined
export const initZanalytics = clientOnly((brand: Brand = 'zillow'): boolean => {
  try {
    initializedBrand = brand;

    if (isInitialized()) {
      getGlobalMonitor()?.count({ name: getCountName('alreadyInitZanalytics') });
      return false;
    }

    if (applyConsentFlag()) {
      stubUa();
      getGlobalMonitor()?.count({ name: getCountName('initZanalytics consentDeclined') });
      return false;
    }

    initZillowAnalytics(window, document, getAnalyticsConfig(brand), 'zanalytics');
    // useful for monitoring/debugging as these counts should match `browser init`
    getGlobalLogger()?.debug(getLogPrefix('zanalytics initialized'), { brand });
    getGlobalMonitor()?.count({ name: getCountName('initZanalytics') });
    return true;
  } catch (error) {
    logError({ error, errorType: getAnalyticsErrorType('InitZanalytics') });
    return false;
  }
});
